import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

export interface SelectedColorsStripProps {
  selectedColors: string[];
  maxSelection: number;
  onRemoveColor: (color: string) => void;
  onClear: () => void;
  /** When true, strip is inside the Create palettes card (box + divider below) */
  insideCard?: boolean;
  /** When true and not insideCard, wrap in same boxed style as selector (for standalone "Your colours" section) */
  boxed?: boolean;
}

const CHIP_SIZE = 48;
const REMOVE_SIZE = 18;

const SelectedColorsStrip: React.FC<SelectedColorsStripProps> = ({
  selectedColors,
  maxSelection,
  onRemoveColor,
  onClear,
  insideCard = false,
  boxed = false,
}) => {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = Platform.OS !== 'web' || width < 768;

  const content = (
    <>
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text }]}>
          Your colours ({selectedColors.length}/{maxSelection})
        </Text>
        {selectedColors.length > 0 && (
          <TouchableOpacity
            onPress={onClear}
            accessibilityLabel="Clear selection"
          >
            <Text style={[styles.clearBtn, { color: colors.primary }]}>
              Clear
            </Text>
          </TouchableOpacity>
        )}
      </View>
      <View style={[styles.chipsRow, isMobile && styles.chipsRowWrap]}>
        {selectedColors.length > 0 ? (
          selectedColors.map((color, index) => (
            <TouchableOpacity
              key={`${color}-${index}`}
              style={[styles.chip, { backgroundColor: color }]}
              onPress={() => onRemoveColor(color)}
              accessibilityLabel={`Remove ${color}`}
            >
              <View style={styles.removeBtn}>
                <Ionicons name="close" size={10} color="white" />
              </View>
            </TouchableOpacity>
          ))
        ) : (
          <Text style={[styles.emptyText, { color: colors.text }]}>
            No colours selected
          </Text>
        )}
      </View>
    </>
  );

  const useBox = insideCard || boxed;
  const boxWrapper = useBox ? (
    <View
      style={[
        styles.selectorBox,
        { backgroundColor: colors.card, borderColor: colors.border },
      ]}
    >
      {content}
    </View>
  ) : (
    content
  );

  return (
    <View
      style={[
        styles.section,
        insideCard && styles.sectionInsideCard,
        insideCard && { borderBottomColor: colors.border + '60' },
        boxed && styles.sectionBoxed,
      ]}
      accessibilityLabel="Your colours"
      accessibilityRole="region"
    >
      {boxWrapper}
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    width: '100%',
    gap: 8,
    marginTop: 24,
    marginBottom: 20,
  },
  sectionInsideCard: {
    marginTop: 0,
    marginBottom: 20,
    paddingBottom: 20,
    borderBottomWidth: 1,
  },
  sectionBoxed: {
    marginTop: 20,
    marginBottom: 20,
  },
  selectorBox: {
    width: '100%',
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    paddingTop: 12,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
  },
  clearBtn: {
    fontSize: 12,
    fontWeight: '600',
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignContent: 'center',
    gap: 8,
  },
  chipsRowWrap: {
    flexWrap: 'wrap',
  },
  chip: {
    width: CHIP_SIZE,
    height: CHIP_SIZE,
    borderRadius: 8,
    position: 'relative',
    borderWidth: 0,
  },
  removeBtn: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: REMOVE_SIZE,
    height: REMOVE_SIZE,
    borderRadius: 9,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    fontStyle: 'italic',
  },
});

export default SelectedColorsStrip;
